import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ADMIN_REFUNDS_QUERY_KEY,
  approveRefund,
  listRefundsForAdmin,
  rejectRefund,
} from "@/api/refundApi";
import { PageHeader } from "@/components/common/PageHeader";
import type { RefundRequest, RefundStatus } from "@/types/domain";

type TabKey = "pending" | "processed" | "rejected" | "failed" | "all";

const tabs: { key: TabKey; label: string; status?: RefundStatus }[] = [
  { key: "pending", label: "Pending", status: "PENDING" },
  { key: "processed", label: "Processed", status: "PROCESSED" },
  { key: "rejected", label: "Rejected", status: "REJECTED" },
  { key: "failed", label: "Failed", status: "FAILED" },
  { key: "all", label: "All" },
];

const statusChipColor: Record<
  RefundStatus,
  "default" | "warning" | "success" | "error"
> = {
  PENDING: "warning",
  PROCESSED: "success",
  REJECTED: "default",
  FAILED: "error",
};

/**
 * Admin review queue for refund requests raised automatically when a patient
 * cancels a paid (CONFIRMED) appointment. Approving here triggers refund processing.
 */
export default function AdminRefundsPage() {
  const qc = useQueryClient();
  const [tabIdx, setTabIdx] = useState(0);
  const currentTab = tabs[tabIdx];

  const [rejecting, setRejecting] = useState<RefundRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [alert, setAlert] = useState<
    { severity: "success" | "error"; text: string } | null
  >(null);

  const { data, isLoading, error } = useQuery({
    queryKey: [...ADMIN_REFUNDS_QUERY_KEY, currentTab.status ?? "ALL"],
    queryFn: () => listRefundsForAdmin(currentTab.status),
  });

  const approveMut = useMutation({
    mutationFn: (id: string) => approveRefund(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_REFUNDS_QUERY_KEY });
      setAlert({
        severity: "success",
        text: "Refund approved and submitted for processing.",
      });
    },
    onError: (e: unknown) => {
      setAlert({
        severity: "error",
        text: extractErrorMessage(e) ?? "Failed to approve refund.",
      });
    },
  });

  const rejectMut = useMutation({
    mutationFn: (args: { id: string; reason: string }) =>
      rejectRefund(args.id, args.reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_REFUNDS_QUERY_KEY });
      setRejecting(null);
      setRejectReason("");
      setAlert({ severity: "success", text: "Refund request rejected." });
    },
    onError: (e: unknown) => {
      setAlert({
        severity: "error",
        text: extractErrorMessage(e) ?? "Failed to reject refund.",
      });
    },
  });

  const rows = useMemo(() => data ?? [], [data]);

  return (
    <Box>
      <PageHeader
        title="Refund requests"
        subtitle="Auto-raised when a patient cancels a paid appointment. Approving here starts the refund to the original payment method."
      />

      {alert && (
        <Alert
          severity={alert.severity}
          onClose={() => setAlert(null)}
          sx={{ mb: 2 }}
        >
          {alert.text}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load refund requests.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tabIdx}
          onChange={(_, v) => setTabIdx(v)}
          variant="scrollable"
          sx={{ px: 1 }}
        >
          {tabs.map((t) => (
            <Tab key={t.key} label={t.label} sx={{ fontWeight: 700 }} />
          ))}
        </Tabs>
      </Paper>

      <Paper variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Requested</TableCell>
              <TableCell>Appointment</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>Doctor</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Payment reference</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Box sx={{ display: "grid", placeItems: "center", py: 3 }}>
                    <CircularProgress size={20} />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" py={2}>
                    No refund requests in this tab.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const canDecide = r.status === "PENDING" || r.status === "FAILED";
              return (
                <TableRow key={r.id}>
                  <TableCell>
                    <Typography variant="body2">
                      {r.requestedAt
                        ? format(new Date(r.requestedAt), "d MMM yyyy")
                        : "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.requestedAt
                        ? format(new Date(r.requestedAt), "h:mm a")
                        : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.appointmentId}>
                      <Typography variant="body2" fontFamily="monospace">
                        {r.appointmentId.slice(0, 8)}…
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.patientId}>
                      <Typography variant="body2" fontFamily="monospace">
                        {r.patientId.slice(0, 8)}…
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={r.doctorId}>
                      <Typography variant="body2" fontFamily="monospace">
                        {r.doctorId.slice(0, 8)}…
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Typography fontWeight={700}>
                      ₹{(r.amountPaise / 100).toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Chip
                        label={r.status}
                        color={statusChipColor[r.status]}
                        size="small"
                        sx={{ fontWeight: 700, width: "fit-content" }}
                      />
                      {r.status === "REJECTED" && r.rejectionReason && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ maxWidth: 200 }}
                        >
                          {r.rejectionReason}
                        </Typography>
                      )}
                      {r.status === "FAILED" && r.failureReason && (
                        <Typography
                          variant="caption"
                          color="error.main"
                          sx={{ maxWidth: 200 }}
                        >
                          {r.failureReason}
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {r.razorpayRefundId ? (
                      <Tooltip title={r.razorpayRefundId}>
                        <Typography variant="body2" fontFamily="monospace">
                          {r.razorpayRefundId}
                        </Typography>
                      </Tooltip>
                    ) : r.razorpayPaymentId ? (
                      <Tooltip title={r.razorpayPaymentId}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontFamily="monospace"
                        >
                          pay: {r.razorpayPaymentId.slice(-8)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {canDecide ? (
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setRejecting(r)}
                          disabled={
                            approveMut.isPending || rejectMut.isPending
                          }
                        >
                          Reject
                        </Button>
                        <Button
                          size="small"
                          color="success"
                          variant="contained"
                          onClick={() => approveMut.mutate(r.id)}
                          disabled={
                            approveMut.isPending &&
                            approveMut.variables === r.id
                          }
                        >
                          {approveMut.isPending &&
                          approveMut.variables === r.id
                            ? "Refunding…"
                            : r.status === "FAILED"
                              ? "Retry"
                              : "Approve"}
                        </Button>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        {r.decidedAt
                          ? `by admin · ${format(
                              new Date(r.decidedAt),
                              "d MMM yyyy",
                            )}`
                          : "—"}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog
        open={Boolean(rejecting)}
        onClose={() => setRejecting(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject refund request</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            This reason is emailed to the patient so they understand why the
            refund was declined.
          </Typography>
          <TextField
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            fullWidth
            multiline
            rows={3}
            autoFocus
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejecting(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!rejectReason.trim() || rejectMut.isPending}
            onClick={() =>
              rejecting &&
              rejectMut.mutate({
                id: rejecting.id,
                reason: rejectReason.trim(),
              })
            }
          >
            {rejectMut.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function extractErrorMessage(e: unknown): string | undefined {
  if (e && typeof e === "object" && "response" in e) {
    const resp = (e as { response?: { data?: unknown } }).response?.data;
    if (resp && typeof resp === "object" && "detail" in resp) {
      const d = (resp as { detail?: unknown }).detail;
      if (typeof d === "string" && d.length > 0) return d;
    }
  }
  if (e instanceof Error) return e.message;
  return undefined;
}
